import { Link, useLocation } from "@tanstack/react-router";
import {
  BookOpenCheck,
  BriefcaseBusiness,
  Building2,
  CalendarDays,
  ChevronDown,
  Handshake,
  MessageSquareQuote,
  ReceiptText,
  ScanLine,
} from "lucide-react";
import { useAuth } from "@core/auth/AuthProvider";
import { VenProLogo } from "@core/components/VenProLogo";
import { getModuleTopbarItems, orderModuleItems } from "@core/module-registry";
import { useOrganization } from "@core/organizations/OrganizationProvider";
import { useAppPreferences } from "@core/preferences/AppPreferencesProvider";

const iconMap = { BriefcaseBusiness, BookOpenCheck, CalendarDays, Handshake, MessageSquareQuote, ReceiptText, ScanLine };
const topbarItems = getModuleTopbarItems();

export function AppHeader() {
  const { isDemoMode } = useAuth();
  const { organizations, activeOrganization, selectOrganization, can, isSuperadmin } = useOrganization();
  const { preferences, isModuleVisible } = useAppPreferences();
  const location = useLocation();

  if (!preferences.topbarVisible) return null;

  const visibleItems = orderModuleItems(
    topbarItems.filter((item) => can(item.permission) && isModuleVisible(item.moduleId)),
    preferences.navigationOrder?.topbar,
  );
  const showLabels = preferences.topbarDisplayMode !== "icon-only";

  return (
    <header className={`vp-topbar vp-control-${preferences.topbarItemSize}`}>
      <div className="vp-topbar-brand-zone">
        <Link to="/jobs" className="vp-logo-link" aria-label="VenPro jobs">
          <VenProLogo className="vp-logo-icon" />
        </Link>
        <label className="vp-topbar-organization">
          <Building2 size={15} aria-hidden="true" />
          <span className="sr-only">Active business</span>
          <select
            value={activeOrganization.id}
            onChange={(event) => selectOrganization(event.target.value)}
            disabled={organizations.length < 2}
          >
            {organizations.map((organization) => (
              <option key={organization.id} value={organization.id}>{organization.name}</option>
            ))}
          </select>
          {organizations.length > 1 && <ChevronDown size={12} aria-hidden="true" />}
        </label>
      </div>

      <nav className="vp-topbar-actions" aria-label="Quick actions">
        {visibleItems.map((item) => {
          const Icon = iconMap[item.icon] ?? BriefcaseBusiness;
          const active = location.pathname === item.href || location.pathname.startsWith(`${item.href}/`);
          return (
            <Link
              key={item.id}
              to={item.href}
              search={item.search}
              className={`vp-topbar-button ${active ? "is-active" : ""}`}
              title={item.label}
            >
              <Icon aria-hidden="true" />
              {showLabels && <span>{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {(isDemoMode || isSuperadmin) && <div className="vp-topbar-utilities">{isDemoMode && <span className="vp-demo-chip">DEMO</span>}{isSuperadmin && <span className="vp-admin-chip">SUPERADMIN</span>}</div>}
    </header>
  );
}
